import { useCallback, useEffect, useMemo, useState } from "react";
import AutoGraph from "@mui/icons-material/AutoGraph";
import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  OrthoApprovalStatus,
  TaskStatus,
  type PlotGeoResponse,
} from "api/model";
import { useOrthoFootprints } from "components/PlotMap/hooks/useOrthoFootprints";
import {
  isStitchedOrthoStatus,
  useCogOrtho,
  type LocationOrtho,
} from "context/CogOrtho";
import {
  formatOrthoFlightLabel,
  getOrthoFlightDate,
  getOrthoFlightTime,
  getOrthoWorkflowKey,
  useOrthoWorkflows,
  type TrialPlotTask,
} from "context/OrthoWorkflow";
import { doesOrthoEnclosePlotGeometry } from "context/PlotMap/orthoPlotEnclosure";

import {
  ANALYSIS_KINDS,
  clearAllAnalyses,
  getAnalysisCellPlan,
  getAnalysisId,
  isAnalysisSelected,
  selectAllAnalyses,
  type AnalysisKind,
} from "./analysisRunPlan";
import AnalysisEnclosingOrthoItem, {
  type AnalysisCellToggle,
} from "./AnalysisEnclosingOrthoItem";
import AnalysisRunStatus from "./AnalysisRunStatus";

interface AnalysisEnclosingOrthosProps {
  plotsGeo: readonly PlotGeoResponse[];
  trialId: number | null;
}

const formatOrthoListLabel = (
  ortho: LocationOrtho,
  orthos: readonly LocationOrtho[],
): string => {
  const dateLabel = getOrthoFlightDate(ortho);
  const hasDuplicateDate = orthos.some(
    (other) => other.id !== ortho.id && getOrthoFlightDate(other) === dateLabel,
  );
  return formatOrthoFlightLabel(
    dateLabel,
    getOrthoFlightTime(ortho),
    hasDuplicateDate,
  );
};

/** Share of an analysis's progress taken up by snipping its plots first. */
const SNIP_PROGRESS_SHARE = 50;

/** 0–100 for a running analysis: snipping fills the first share, processing the rest. */
const getActiveAnalysisProgress = (task: TrialPlotTask): number => {
  if (task.snipProgress != null) {
    return (task.snipProgress * SNIP_PROGRESS_SHARE) / 100;
  }
  // A start request still in flight hasn't begun snipping.
  return task.isStarting
    ? 0
    : SNIP_PROGRESS_SHARE +
        ((task.progress ?? 0) * (100 - SNIP_PROGRESS_SHARE)) / 100;
};

const AnalysisEnclosingOrthos: React.FC<AnalysisEnclosingOrthosProps> = ({
  plotsGeo,
  trialId: selectedTrialId,
}) => {
  const {
    actions,
    byKey,
    metadata: { knownOrthoMeta },
  } = useOrthoWorkflows();
  // Fresh analyses start selected and failed or completed ones start unselected,
  // so track which have been flipped from that default.
  const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set());
  // The run just started from this tab; kept so the totals survive tasks finishing.
  const [run, setRun] = useState<{ startedAt: Date; total: number } | null>(
    null,
  );
  const { orthos } = useCogOrtho();
  const stitchedOrthos = useMemo(
    () => orthos.filter((ortho) => isStitchedOrthoStatus(ortho.status)),
    [orthos],
  );
  const { isPending } = useOrthoFootprints(
    stitchedOrthos,
    stitchedOrthos.length > 0,
  );

  const enclosingOrthos = stitchedOrthos.filter((ortho) => {
    const footprint = knownOrthoMeta[ortho.id]?.boundary;
    if (footprint == null) {
      return false;
    }
    return doesOrthoEnclosePlotGeometry(footprint, plotsGeo);
  });

  const toggle = useCallback((id: string) => {
    setToggled((current) => {
      const next = new Set(current);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const rows =
    selectedTrialId == null
      ? []
      : enclosingOrthos
          .filter((ortho) => ortho.status === OrthoApprovalStatus.approved)
          .map((ortho) => {
            const key = getOrthoWorkflowKey(ortho);
            return {
              key,
              plans: ANALYSIS_KINDS.map((kind) => ({
                id: getAnalysisId(key, kind),
                kind,
                plan: getAnalysisCellPlan(
                  byKey.get(key),
                  selectedTrialId,
                  kind,
                ),
              })),
            };
          });
  const selectionEntries = rows.flatMap(({ plans }) => plans);
  const selectedToRun = rows.flatMap(({ key, plans }) =>
    plans
      .filter(
        ({ id, plan }) =>
          plan.isSelectable && isAnalysisSelected(plan, toggled.has(id)),
      )
      .map((entry) => ({ ...entry, key })),
  );

  const active = rows.flatMap(({ plans }) =>
    plans.filter(({ plan }) => plan.task.isRunning),
  );
  // Analyses wait on the plots being snipped before they start processing.
  const snippingCount = active.filter(
    ({ plan }) => plan.task.status === TaskStatus.waiting,
  ).length;
  const totalCount = Math.max(run?.total ?? 0, active.length);
  const finishedCount = totalCount - active.length;
  const progress =
    totalCount === 0
      ? 0
      : (finishedCount * 100 +
          active.reduce(
            (sum, { plan }) => sum + getActiveAnalysisProgress(plan.task),
            0,
          )) /
        totalCount;
  // Without a run (e.g. after reopening the tab), fall back to the earliest running task.
  const startedAt =
    run?.startedAt ??
    active
      .flatMap(({ plan }) =>
        plan.task.task != null ? [new Date(plan.task.task.created_at)] : [],
      )
      .reduce<Date | null>(
        (earliest, date) =>
          Number.isNaN(date.getTime()) || (earliest != null && earliest <= date)
            ? earliest
            : date,
        null,
      );

  useEffect(() => {
    if (run != null && active.length === 0) {
      setRun(null);
    }
  }, [active.length, run]);

  const runSelected = () => {
    if (selectedTrialId == null) return;
    for (const { key, kind } of selectedToRun) {
      if (kind === "canopy") {
        actions.startAnalyzeCanopy(key, selectedTrialId);
      } else {
        actions.startAnalyzeEmergence(key, selectedTrialId);
      }
    }
    // Retries are one-shot: they go back to unselected once they've run.
    setToggled((current) => {
      const next = new Set(current);
      for (const { id } of selectedToRun) next.delete(id);
      return next;
    });
    setRun({ startedAt: new Date(), total: selectedToRun.length });
  };

  const hasSelection = selectedToRun.length > 0;
  const hasSelectable = selectionEntries.some(
    ({ plan }) => plan.isSelectable && !plan.needsOptIn,
  );
  const toggleSelectAll = () => {
    setToggled((current) =>
      hasSelection
        ? clearAllAnalyses(selectionEntries, current)
        : selectAllAnalyses(selectionEntries, current),
    );
  };

  const getToggles = (ortho: LocationOrtho) => {
    const workflowKey = getOrthoWorkflowKey(ortho);
    const toggles = {} as Record<AnalysisKind, AnalysisCellToggle>;
    for (const kind of ANALYSIS_KINDS) {
      const id = getAnalysisId(workflowKey, kind);
      toggles[kind] = {
        isToggled: toggled.has(id),
        onToggle: () => {
          toggle(id);
        },
      };
    }
    return toggles;
  };

  return (
    <Stack spacing={0.5}>
      {active.length === 0 && (
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography component="h3" variant="subtitle2">
            Orthos covering plots
          </Typography>
          {!isPending && enclosingOrthos.length > 0 && (
            <Button
              disabled={!hasSelection && !hasSelectable}
              onClick={toggleSelectAll}
              size="small"
            >
              {hasSelection ? "Clear all" : "Select all"}
            </Button>
          )}
        </Stack>
      )}
      {isPending ? (
        <CircularProgress aria-label="Loading ortho footprints" size={24} />
      ) : active.length > 0 ? (
        <AnalysisRunStatus
          finishedCount={finishedCount}
          progress={progress}
          snippingCount={snippingCount}
          startedAt={startedAt}
          totalCount={totalCount}
        />
      ) : enclosingOrthos.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {stitchedOrthos.length === 0
            ? "No orthos are available for this location."
            : "No orthos completely enclose this plot geometry."}
        </Typography>
      ) : (
        <Stack spacing={1}>
          <TableContainer component={Paper} variant="outlined">
            <Table
              aria-label="Orthos that completely enclose plot geometry"
              size="small"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ py: 0.5 }}>Ortho</TableCell>
                  <TableCell align="center" sx={{ px: 0.5, py: 0.5 }}>
                    Canopy
                  </TableCell>
                  <TableCell align="center" sx={{ px: 0.5, py: 0.5 }}>
                    Emergence
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedTrialId != null &&
                  enclosingOrthos.map((ortho) => (
                    <AnalysisEnclosingOrthoItem
                      key={ortho.id}
                      label={formatOrthoListLabel(ortho, stitchedOrthos)}
                      ortho={ortho}
                      toggles={getToggles(ortho)}
                      trialId={selectedTrialId}
                      workflow={byKey.get(getOrthoWorkflowKey(ortho))}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography color="text.secondary" variant="caption">
            {selectedToRun.length} to run
          </Typography>
          <Typography variant="body2">
            Canopy measures cover per plot; emergence counts plants while the
            canopy is still open. Each is run per ortho.
          </Typography>
          <Button
            disabled={selectedToRun.length === 0}
            fullWidth
            onClick={runSelected}
            startIcon={<AutoGraph />}
            variant="contained"
          >
            Run analysis · {selectedToRun.length}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default AnalysisEnclosingOrthos;
